import React, { useState } from "react";
import { useParams } from "react-router-dom";

import Spacer from "../../../../components/Spacer";
import ComponentLoader from "../../../../components/Spinner/ComponentLoader";
import WorkspaceDetailsBreadcrumbs from "../components/WorkspaceDetailsBreadcrumbs";
import WorkspaceDetailsEmptyState from "../components/WorkspaceDetailsEmptyState";
import { WorkspaceDetailsFilterPanel } from "../components/WorkspaceDetailsFilterPanel";
import WorkspaceDetailsHeader from "../components/WorkspaceDetailsHeader";
import WorkspaceDetailsMeetingList from "../components/WorkspaceDetailsMeetingList";

import ErrorPage from "../../../../pages/Error";

import { useCurrentUser, useMeetingWorkspaceDetails, useMeetingsSearch } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import useForm from "../../../../hooks/useForm";
import { useWorkspaceDetailsLogic } from "../hooks";

import { convertFiltersToSearchParams } from "../utils";

import BasePageTemplate from "../../../../pageTemplates/BasePageTemplate";
import useInfoPageTemplateQueryState from "../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { config } from "../config";
import { type WorkspaceDetailsFilters, WorkspaceDetailsFiltersSchema } from "../schema";

// Admin filters type excludes privateNotes but includes date filters
type AdminWorkspaceDetailsFilters = Omit<WorkspaceDetailsFilters, "privateNotes">;

const AdminWorkspaceDetails = () => {
	const { workspaceId } = useParams();

	const { data: currentUser } = useCurrentUser();
	const { data: workspace } = useMeetingWorkspaceDetails(workspaceId || "");

	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

	// Query state for the page (filters, sort, page) - using admin filters
	const { filters, setFilters, sort, pageNumber, setPageNumber } =
		useInfoPageTemplateQueryState<AdminWorkspaceDetailsFilters>({
			defaultSortName: config.defaultSortName,
			defaultSortDirection: config.defaultSortDirection,
			defaultPageNumber: config.defaultPageNumber,
		});

	const userId = currentUser?.me?.authorization?.payload.user_id || "";

	// Meetings search service
	const {
		data: meetings,
		totalCount: totalMeetingsCount,
		isLoading: isFetchingMeetings,
	} = useMeetingsSearch<"workspaceDetails">(
		convertFiltersToSearchParams(filters, sort, pageNumber, workspaceId || "", userId)
	);

	const {
		state: filterFormState,
		updateForm: updateFilterForm,
		setInitialState: setInitialFilterForm,
	} = useForm({
		schema: WorkspaceDetailsFiltersSchema.omit({ privateNotes: true }), // Admin schema excludes privateNotes
		defaultValues: filters,
		disableWarning: true,
	});

	const { value: ffMeetings } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	// Use shared logic hook
	const {
		expandedMeetingIds,
		handleToggleAccordion,
		transformWorkspaceParticipants,
		calculateActiveFiltersCount,
		getHasFiltersApplied,
	} = useWorkspaceDetailsLogic(meetings);

	// Only show loading for meetings when filters change, since initial data is prefetched
	if (isFetchingMeetings) {
		return <ComponentLoader />;
	}

	// Show access denied page if feature flag is disabled
	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	// Explicit admin role validation - only admins should access this view
	const userRole = currentUser?.me?.authorization?.payload.user_primary_role || "";
	const adminRoles = ["billing-admin", "admin", "basic-admin"];
	const isAdmin = adminRoles.includes(userRole);

	if (!isAdmin) {
		return <ErrorPage code={403} />;
	}

	// Transform workspace participants for AvatarGroup
	const workspaceParticipants = transformWorkspaceParticipants(workspace?.participants);

	// Count active filters for the filter button badge
	const activeFiltersCount = calculateActiveFiltersCount(filters);

	// Determine if any filters are applied (admin only has status filters)
	const hasFiltersApplied = getHasFiltersApplied(filters, true);

	return (
		<>
			<BasePageTemplate title={workspace?.name || "Meeting Workspace"}>
				{/* Admin-specific breadcrumbs */}
				<WorkspaceDetailsBreadcrumbs
					workspaceName={workspace?.name || ""}
					workspaceId={workspaceId || ""}
					isAdmin={true}
				/>

				{/* Header with workspace name, participants, and filter button (no add meeting for admin) */}
				<WorkspaceDetailsHeader
					workspaceName={workspace?.name || ""}
					workspaceParticipants={workspaceParticipants}
					activeFiltersCount={activeFiltersCount}
					onFilterClick={() => setIsFilterPanelOpen(true)}
					hasExistingMeetings={meetings?.length > 0}
					isAdmin={true}
					workspaceId={workspaceId || ""}
					filters={filters}
					sort={sort}
					meetings={meetings}
				/>

				<Spacer height={2} />

				{/* Main content */}
				{meetings && meetings.length > 0 ? (
					<WorkspaceDetailsMeetingList
						meetings={meetings}
						workspace={workspace!}
						expandedMeetingIds={expandedMeetingIds}
						handleToggleAccordion={handleToggleAccordion}
						userId={userId}
						totalMeetingsCount={totalMeetingsCount}
						pageNumber={pageNumber}
						setPageNumber={setPageNumber}
						pageSize={config.pageSize}
						isAdmin={true}
						setEditingMeeting={() => {}} // Disabled for admin
						setUserAssignment={() => {}} // Disabled for admin
					/>
				) : (
					<WorkspaceDetailsEmptyState hasFiltersApplied={hasFiltersApplied} isAdmin={true} />
				)}
			</BasePageTemplate>

			{/* Admin filter panel */}
			<WorkspaceDetailsFilterPanel
				isOpen={isFilterPanelOpen}
				onClose={() => setIsFilterPanelOpen(false)}
				onApplyFilters={(newFilters) => {
					setFilters(newFilters);
					setInitialFilterForm(newFilters, true);
				}}
				filters={filterFormState}
				updateForm={updateFilterForm}
				isAdminView={true}
			/>
		</>
	);
};

export default AdminWorkspaceDetails;
